export const PRODUCT_GROUP_LEAF_FIELDS_FRAGMENT = `
  fragment ProductGroupLeafFields on Metaobject {
    solids: field(key: "solids") {
      references(first: 250) {
        nodes {
          ... on Product {
            id
          }
        }
      }
    }
    florals: field(key: "florals") {
      references(first: 250) {
        nodes {
          ... on Product {
            id
          }
        }
      }
    }
  }
`;

// Just enough to paint a swatch dot and rank it by colour family. The PLP's
// style group index fetches this for every grouped product in a collection,
// so a card's extra swatches need no follow-up request.
export const PRODUCT_SWATCH_DOT_FIELDS_FRAGMENT = `
  fragment ProductSwatchDotFields on Product {
    title
    handle
    availableForSale
    colorPattern: metafield(namespace: "shopify", key: "color-pattern") {
      references(first: 1) {
        nodes {
          ... on Metaobject {
            swatchColor: field(key: "color") {
              value
            }
            swatchLabel: field(key: "label") {
              value
            }
            swatchImage: field(key: "image") {
              reference {
                ... on MediaImage {
                  image {
                    url(transform: { maxWidth: 64 })
                  }
                }
              }
            }
          }
        }
      }
    }
    colorFamilyList: metafield(namespace: "custom", key: "color_family_list") {
      references(first: 10) {
        nodes {
          ... on Metaobject {
            id
          }
        }
      }
    }
  }
`;

export const PRODUCT_GROUP_SWATCH_EXPANSION_FRAGMENT = `
  fragment ProductGroupSwatchExpansionFields on Metaobject {
    solids: field(key: "solids") {
      references(first: 250) {
        nodes {
          ... on Product {
            id
            title
            handle
            availableForSale
            featuredImage {
              url
            }
            ...ProductColorPatternFields
            fabricNew: metafield(namespace: "custom", key: "fabric_new") {
              ...ProductFabricReferenceFields
            }
            fabric: metafield(namespace: "custom", key: "fabric") {
              ...ProductFabricReferenceFields
            }
            colorFamilyList: metafield(namespace: "custom", key: "color_family_list") {
              references(first: 10) {
                nodes {
                  ... on Metaobject {
                    position: field(key: "position") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    florals: field(key: "florals") {
      references(first: 250) {
        nodes {
          ... on Product {
            id
            title
            handle
            availableForSale
            featuredImage {
              url
            }
            ...ProductColorPatternFields
            fabricNew: metafield(namespace: "custom", key: "fabric_new") {
              ...ProductFabricReferenceFields
            }
            fabric: metafield(namespace: "custom", key: "fabric") {
              ...ProductFabricReferenceFields
            }
            colorFamilyList: metafield(namespace: "custom", key: "color_family_list") {
              references(first: 10) {
                nodes {
                  ... on Metaobject {
                    position: field(key: "position") {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT = `
  fragment ProductColorPatternFields on Product {
    colorPattern: metafield(namespace: "shopify", key: "color-pattern") {
      references(first: 1) {
        nodes {
          ... on Metaobject {
            swatchColor: field(key: "color") {
              value
            }
            swatchLabel: field(key: "label") {
              value
            }
            swatchImage: field(key: "image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
          }
        }
      }
    }
    # The same Shop by Color family filter is driven off this metafield (see
    # COLOR_FAMILY_KEY in api-facets.js). It's a list.metaobject_reference field — each
    # product points at one or more "Color Family - List" metaobject entries (Pinks, Reds,
    # etc.), not plain text — so this resolves those references' ids, the same ids the
    # active family filter itself matches against. Used to dynamically match a grouped
    # card's padded swatches against whichever family is currently filtered, instead of a
    # hardcoded per-family color order.
    colorFamilyList: metafield(namespace: "custom", key: "color_family_list") {
      references(first: 10) {
        nodes {
          ... on Metaobject {
            id
            handle
            colorFamilyName: field(key: "color_family_name") {
              value
            }
            position: field(key: "position") {
              value
            }
            swatchColor: field(key: "swatch_color") {
              value
            }
            swatchImage: field(key: "swatch_image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Handle + label of a product's fabric metaobject; covers single and list references.
export const PRODUCT_FABRIC_REFERENCE_FIELDS_FRAGMENT = `
  fragment ProductFabricReferenceFields on Metafield {
    reference {
      ... on Metaobject {
        handle
        label: field(key: "label") {
          value
        }
      }
    }
    references(first: 1) {
      nodes {
        ... on Metaobject {
          handle
          label: field(key: "label") {
            value
          }
        }
      }
    }
  }
`;
